import styles from "../css/components/sidebar.module.css"
import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"

import {
  LayoutDashboard,
  Home,
  Building2,
  Users,
  Package,
  FileText,
  RefreshCw,
  Truck,
  Clock,
  Leaf,
  ShoppingCart,
  Pill,
  Hospital,
  CreditCard,
  BarChart3,
  TrendingUp,
  LineChart,
  RotateCcw,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
} from "lucide-react"
import { useSidebar } from "../context/SidebarContext"
import { useAuth } from "../context/AuthContext"
import { fetchExpiryStock } from "../api/Inventory/ExpiryStockApi.js"
import { fetchStockTransfers } from "../api/Inventory/StockTransferApi.js"
import { getBranchByCode, getBranches } from "../api/branchManagement]/branchApi.js"
import Dropdown from "./Dropdown"

export default function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isCollapsed, setIsCollapsed, openMenus, toggleMenu, activeItem, setActiveItem } = useSidebar()

  const { user, updateUser } = useAuth()
  const roleId = Number(user?.roleid)

  const supplierLabel = (roleId === 3 || roleId === 8) ? "Supplier Rating" : "Supplier Management"

  const userBranchCode = user?.branchcode ? String(user.branchcode) : null

  const [branchName, setBranchName] = useState("")
  const [availableBranches, setAvailableBranches] = useState([])

  const effectiveBranchCode = userBranchCode

  const branchLabel = branchName || effectiveBranchCode || ""

  const branchLabelParts = String(branchLabel).trim().split(/\s+/).filter(Boolean)
  const branchLabelTop = branchLabelParts.slice(0, 2).join(" ")
  const branchLabelBottom = branchLabelParts.slice(2).join(" ")

  const [expiredStockCount, setExpiredStockCount] = useState(null)
  const [stockTransferApprovedCount, setStockTransferApprovedCount] = useState(null)

  useEffect(() => {
    let cancelled = false

    const loadExpiredCount = async () => {
      try {
        const data = await fetchExpiryStock()
        if (cancelled) return
        const count = Array.isArray(data)
          ? data.filter((row) => String(row?.priority).toLowerCase() === "expired").length
          : 0
        setExpiredStockCount(count)
      } catch (err) {
        if (!cancelled) {
          setExpiredStockCount(null)
        }
      }
    }

    // Only load if user can have inventory context
    if (roleId) {
      loadExpiredCount()
    }

    return () => {
      cancelled = true
    }
  }, [roleId, effectiveBranchCode])

  useEffect(() => {
    let cancelled = false

    const loadBranchName = async () => {
      if (!effectiveBranchCode) {
        setBranchName("")
        return
      }
      try {
        const data = await getBranchByCode(effectiveBranchCode)
        if (cancelled) return
        setBranchName(data?.branchname ? String(data.branchname) : "")
      } catch (_) {
        if (!cancelled) setBranchName("")
      }
    }

    loadBranchName()
    return () => {
      cancelled = true
    }
  }, [effectiveBranchCode])

  // Load full branch list for Directors so they can switch context
  useEffect(() => {
    if (roleId !== 2) return
    let cancelled = false

    const loadBranches = async () => {
      try {
        const data = await getBranches()
        if (cancelled) return
        setAvailableBranches(Array.isArray(data) ? data : [])
      } catch {
        if (!cancelled) setAvailableBranches([])
      }
    }

    loadBranches()
    return () => {
      cancelled = true
    }
  }, [roleId])

  useEffect(() => {
    let cancelled = false

    const loadApprovedStockTransferCount = async () => {
      try {
        const data = await fetchStockTransfers()
        if (cancelled) return

        const allowedDepartments = ["Dispensary", "Front Shop", "OTC", "Clinic"]

        const count = Array.isArray(data)
          ? data.filter((row) => {
              const status = String(row?.status || "").toLowerCase()
              if (status !== "approved") return false

              const dept = String(row?.department || "")
              if (!allowedDepartments.includes(dept)) return false

              if (!effectiveBranchCode) return true
              const toBranchCode = row?.toBranchCode != null ? String(row.toBranchCode) : ""
              return toBranchCode === effectiveBranchCode
            }).length
          : 0

        setStockTransferApprovedCount(count)
      } catch (err) {
        if (!cancelled) {
          setStockTransferApprovedCount(null)
        }
      }
    }

    if (roleId) {
      loadApprovedStockTransferCount()
    }

    return () => {
      cancelled = true
    }
  }, [roleId, effectiveBranchCode])

  const pharmacyAdminItems = [
    { icon: <LayoutDashboard size={18} />, text: "Dashboard", path: "/dashboard" },
    {
      icon: <ShoppingCart size={18} />,
      text: "Customer Management",
      children: [
        { icon: <Users size={16} />, text: "Profiles", path: "/customer/profiles" },
        { icon: <Pill size={16} />, text: "Dispensary", path: "/customer/dispensary" },
        { icon: <Hospital size={16} />, text: "Clinic", path: "/customer/clinic" },
      ],
    },
    { icon: <CreditCard size={18} />, text: "Debtors", path: "/credits/debtors" },
    {
      icon: <Package size={18} />,
      text: "Inventory Management",
      path: "/inventory",
      children: [
        { icon: <FileText size={16} />, text: "Receive Stock", path: "/inventory/receive-stock" },
        { icon: <RefreshCw size={16} />, text: "Reverse Stock", path: "/inventory/reverse-stock" },
        { icon: <Truck size={16} />, text: (roleId === 2 || roleId === 3 || roleId === 8) ? "Stock Request" : "Stock Transfer", path: (roleId === 2 || roleId === 3 || roleId === 8) ? "/inventory/stock-request" : "/inventory/stock-transfer" },
        {
          icon: <Clock size={16} />,
          text: "Expired Stock",
          path: "/inventory/expired-stock",
          badge:
            typeof expiredStockCount === "number" && expiredStockCount > 0
              ? expiredStockCount
              : undefined,
        },
        { icon: <Leaf size={16} />, text: "Eco Control", path: (roleId === 3 || roleId === 8) ? "/inventory/eco-control-fridges" : "/inventory/eco-control-sensors" },
      ],
    },
    { icon: <Truck size={18} />, text: "Supplier List", path: "/supplier-management" },
    {
      icon: <BarChart3 size={18} />,
      text: "Analytics",
      path: "/analytics",
    },
  ]

  const defaultItems = [
    { icon: <LayoutDashboard size={18} />, text: "Dashboard", path: "/dashboard" },
    roleId === 2 ? { icon: <Building2 size={18} />, text: "Company Management", path: "/company-management" } : null,
    roleId === 2 ? { icon: <Home size={18} />, text: "Branch Management", path: "/branch-management" } : null,

    roleId === 2 ? { icon: <Users size={18} />, text: "User Management", path: "/user-management" } : null,
    {
      icon: <ShoppingCart size={18} />,
      text: "Customer Management",
      children: [
        { icon: <Users size={16} />, text: "Profiles", path: "/customer/profiles" },
        { icon: <Pill size={16} />, text: "Dispensary", path: "/customer/dispensary" },
        { icon: <Hospital size={16} />, text: "Clinic", children: [
          { icon: <Clock size={14} />, text: "Booking", path: "/customer/clinic/booking" },
          { icon: <FileText size={14} />, text: "Consultations", path: "/customer/clinic/consultations" },
          { icon: <RotateCcw size={14} />, text: "Consultation History", path: "/customer/clinic/consultation-history" },
          { icon: <Package size={14} />, text: "Services", path: "/customer/clinic/services" },
          { icon: <FileText size={14} />, text: "DoH Guideline", path: "/customer/clinic/doh-guideline" },
        ]},
      ],
    },
    { icon: <CreditCard size={18} />, text: "Debtors", path: "/credits/debtors" },
    {
      icon: <Package size={18} />,
      text: "Inventory Management",
      path: "/inventory",
      children: [
        { icon: <FileText size={16} />, text: "Receive Stock", path: "/inventory/receive-stock" },
        { icon: <RefreshCw size={16} />, text: "Reverse Stock", path: "/inventory/reverse-stock" },
        { icon: <Truck size={16} />, text: (roleId === 2 || roleId === 3 || roleId === 8) ? "Stock Request" : "Stock Transfer", path: (roleId === 2 || roleId === 3 || roleId === 8) ? "/inventory/stock-request" : "/inventory/stock-transfer" },
        {
          icon: <Clock size={16} />,
          text: "Expired Stock",
          path: "/inventory/expired-stock",
          badge:
            typeof expiredStockCount === "number" && expiredStockCount > 0
              ? expiredStockCount
              : undefined,
        },
        { icon: <Leaf size={16} />, text: "Eco Control", path: (roleId === 3 || roleId === 8) ? "/inventory/eco-control-fridges" : "/inventory/eco-control-sensors" },
      ],
    },
    roleId === 2 ? {
      icon: <Truck size={18} />,
      text: "Supplier Management",
      children: [
        { icon: <Truck size={16} />, text: "Supplier List", path: "/supplier-management" },
        { icon: <Truck size={16} />, text: "Supplier Rating", path: "/supplier-ratings" },
      ],
    } : null,
    (roleId === 3 || roleId === 8) ? { icon: <Truck size={18} />, text: "Supplier Rating", path: "/supplier-ratings" } : null,
    (roleId !== 3 && roleId !== 2 && roleId !== 8) ? { icon: <Truck size={18} />, text: "Supplier List", path: "/supplier-management" } : null,
    {
      icon: <BarChart3 size={18} />,
      text: "Analytics",
      path: "/analytics",
    },
  ].filter(Boolean)

  // Role-based pruning of menu items
  const processedDefaultItems = defaultItems
    .map((item) => {
      if (!item) return item
      // Customer Management children filters per role
      if (item.text === "Customer Management" && Array.isArray(item.children)) {
        let filteredChildren = item.children
        // Only allow Profiles card for roles: Director(2), RP(3), Nurse(6), PCDT(8)
        const allowedProfilesRoles = [2, 3, 6, 8]
        if (!allowedProfilesRoles.includes(roleId)) {
          filteredChildren = filteredChildren.filter((c) => c.text !== "Profiles")
        }
        if (roleId === 7) {
          // hide Clinic
          filteredChildren = filteredChildren.filter((c) => c.text !== "Clinic")
        } else if (roleId === 3) {
          // hide Clinic for role 3
          filteredChildren = filteredChildren.filter((c) => c.text !== "Clinic")
        } else if (roleId === 8) {
          // allow Credit for role 8 (no filtering)
        } else if (roleId === 6) {
          // hide Dispensary
          filteredChildren = filteredChildren.filter((c) => c.text !== "Dispensary")
        }

        // Handle nested Clinic children filtering
        const clinicItem = filteredChildren.find((c) => c.text === "Clinic")
        if (clinicItem && clinicItem.children) {
          let clinicChildren = clinicItem.children
          if (roleId === 7) {
            // role 7: hide all Clinic sub-items
            clinicChildren = []
          } else if (roleId === 3) {
            // role 3: hide all Clinic sub-items
            clinicChildren = []
          }
          // Update the Clinic item with filtered children
          const clinicIndex = filteredChildren.findIndex((c) => c.text === "Clinic")
          if (clinicIndex !== -1) {
            filteredChildren[clinicIndex] = { ...clinicItem, children: clinicChildren }
          }
        }

        return { ...item, children: filteredChildren }
      }
      // Inventory Management children filters per role
      if (item.text === "Inventory Management" && Array.isArray(item.children)) {
        let filteredChildren = item.children
        // Hide "Receive Stock" for role 3
        if (roleId === 3) {
          filteredChildren = filteredChildren.filter((c) => c.text !== "Receive Stock")
        }
        return { ...item, children: filteredChildren }
      }
      return item
    })
    .filter((item) => {
      if (!item) return false
      // Prune top-level items for role 7
      if (roleId === 7) {
        if (item.path === "/inventory") return false // hide Inventory Management
        if (item.path === "/supplier-management") return false // hide Supplier Management
        if (item.path === "/supplier-ratings") return false // hide Supplier Rating
        if (item.path === "/analytics") return false // hide Analytics
        if (item.path === "/credits/debtors") return false // hide Debtors
      }
      // Prune Supplier Management and Supplier Rating for role 6
      if (roleId === 6) {
        if (item.path === "/supplier-management") return false
        if (item.path === "/supplier-ratings") return false
        if (item.path === "/credits/debtors") return false
      }
      // For roles 6 and 8, top-level exclusions are handled above (children),
      // Branch/User Management are only present for role 2 already.
      return true
    })

  const sidebarItems = roleId === 4 ? pharmacyAdminItems : processedDefaultItems

  useEffect(() => {
    const path = String(location?.pathname || "")

    const findActiveTextByPath = (items) => {
      for (const item of items) {
        if (!item) continue
        if (item.path && path.startsWith(item.path)) return item.text
        if (Array.isArray(item.children)) {
          const childMatch = findActiveTextByPath(item.children)
          if (childMatch) return childMatch
        }
      }
      return null
    }

    const match = findActiveTextByPath(sidebarItems)
    if (match && match !== activeItem) {
      setActiveItem(match)
      return
    }

    if (!match && path.startsWith("/dashboard") && activeItem !== "Dashboard") {
      setActiveItem("Dashboard")
    }
  }, [activeItem, location?.pathname, setActiveItem, sidebarItems])

  const clearScriptDraft = () => {
    try {
      sessionStorage.removeItem("currentScript")
      sessionStorage.removeItem("cameFromPriceSummary")
      sessionStorage.removeItem("cameFromOTCSummary")
      sessionStorage.removeItem("priceScriptActive")
    } catch {}
  }

  // Handle top-level clicks
  const handleClick = (item) => {
    setActiveItem(item.text)
    if (item.path) {
      clearScriptDraft()
      navigate(item.path)
    }
    if (item.children) {
      toggleMenu(item.text) // also toggle menu when name is clicked
    }
  }

  // Handle chevron clicks for menu toggling
  const handleChevronClick = (e, item) => {
    e.stopPropagation()
    if (item.children) {
      toggleMenu(item.text) // top-level toggle
    }
  }

  // Handle submenu clicks
  const handleSubItemClick = (child, parentText) => {
    if (child.path) {
      setActiveItem(child.text)
      clearScriptDraft()
      navigate(child.path)
    }

    if (child.children && child.children.length > 0) {
      toggleMenu(child.text, parentText) // nested toggle with parent
      return
    }
  }

  return (
    <nav className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ""}`}>
      <div className={styles.sidebarHeader}>
        <div className={styles.logo}>
          {!isCollapsed && (
            roleId === 2 && availableBranches.length > 0 ? (
              <Dropdown
                name="director-branch-selector"
                options={availableBranches.map((b) => b.branchname)}
                value={
                  availableBranches.find((b) => String(b.branchcode) === effectiveBranchCode)?.branchname || ""
                }
                placeholder="Select Branch"
                onChange={(branchNameOption) => {
                  const branch = availableBranches.find((b) => b.branchname === branchNameOption)
                  if (!branch) return
                  const code = String(branch.branchcode)
                  // Update global auth user so all filtering uses this branch
                  updateUser({ branchcode: code })
                  setBranchName(branch.branchname ? String(branch.branchname) : "")
                }}
                className=""
              />
            ) : (
              <div className={styles.branchName}>
                <span className={styles.branchNameTop}>{branchLabelTop}</span>
                {branchLabelBottom ? (
                  <span className={styles.branchNameBottom}>{branchLabelBottom}</span>
                ) : null}
              </div>
            )
          )}
        </div>
        <button className={styles.toggleButton} onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      <div className={styles.sidebarContent}>
        <ul className={styles.sidebarList}>
          {sidebarItems.map((item, idx) => (
            <li key={idx} className={styles.sidebarItem}>
              <div
                className={`${styles.sidebarLink} ${activeItem === item.text ? styles.active : ""}`}
                onClick={() => handleClick(item)}
              >
                <span className={styles.icon}>{item.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className={styles.text}>{item.text}</span>
                    <div className={styles.linkActions}>
                      {item.badge && <span className={styles.badge}>{item.badge}</span>}
                      {item.children && (
                        <span className={styles.chevron} onClick={(e) => handleChevronClick(e, item)}>
                          {openMenus[item.text] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {item.children && openMenus[item.text] && !isCollapsed && (
                <ul className={styles.subMenu}>
                  {item.children.map((child, i) => {
                    const hasNested = !!(child.children && child.children.length > 0)
                    const childIsActive = activeItem === child.text

                    return (
                      <li
                        key={i}
                        className={`${styles.subItem} ${childIsActive ? styles.active : ""}`}
                        onClick={() => handleSubItemClick(child, item.text)}
                      >
                        <span className={styles.icon}>{child.icon}</span>
                        <span className={styles.text}>{child.text}</span>

                        <div className={styles.linkActions}>
                          {child.badge && <span className={styles.badge}>{child.badge}</span>}
                          {hasNested && (
                            <span className={styles.chevron} onClick={(e) => {
                              e.stopPropagation()
                              toggleMenu(child.text, item.text)
                            }}>
                              {openMenus[`${item.text}/${child.text}`] ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </span>
                          )}
                        </div>

                        {hasNested && openMenus[`${item.text}/${child.text}`] && (
                          <ul className={styles.subMenu}>
                            {child.children.map((grand, k) => (
                              <li
                                key={k}
                                className={`${styles.subItem} ${activeItem === grand.text ? styles.active : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleSubItemClick(grand, child.text)
                                }}
                              >
                                <span className={styles.icon}>{grand.icon}</span>
                                <span className={styles.text}>{grand.text}</span>
                                {grand.badge && <span className={styles.badge}>{grand.badge}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </nav>
  )
}